import { vec2 } from 'gl-matrix';
import { domainEventBus } from '~/domain/events';
import {
    WindowLevelDeltaEvent,
    WindowLevelSetEvent,
} from '~/domain/events/WindowingDomainEvents';
import { WindowLevel } from '~/domain/patient/windowLevel';
import { fusionColorMappingById } from '~/domain/viewport/fusionBlend';
import {
    WindowingCursor,
    WindowingCursorOptions,
} from '../../data/common/customs/cursors/WindowingCursor';
import { Environment } from '../../data/Environment';
import {
    FusedImageDisplay,
    fusedPairOf,
    ownFusedPair,
    ViewFusion,
    windowingSeriesUidFor,
} from '../../view/layers/fusedImageDisplay';
import { ModifierKeys } from '../ModifierKeys';
import { PRIMARY_COLOR, SECONDARY_COLOR } from '~/theme/theme';
import { vec3ToHex } from '~/utils/colorUtils';

/**
 * Configuration for the window/level drag.
 */
const WINDOWING_CONFIG = {
    /** Which mouse axis adjusts window width. */
    widthAxis: 'x' as 'x' | 'y',
    /** Which mouse axis adjusts window center. */
    centerAxis: 'y' as 'x' | 'y',
    /** 'proportional': delta = movement * sensitivity.
     *  'direction': delta = +/-stepSize per frame (sign-only). */
    sensitivityMode: 'proportional' as 'proportional' | 'direction',
    /** Multiplier for proportional mode. */
    sensitivity: 1.5,
    /** Fixed step for direction mode (matches existing
     *  WindowingAction). */
    stepSize: 3,
};

/**
 * The window/level drag: which image it adjusts, the window level to
 * restore on cancel, the sun cursor overlay that stands in for the
 * pointer, and the mapping from pointer movement to window/level deltas.
 *
 * Driven by {@link GlobalRightButtonCoordinator}, which owns the button
 * and pointer lock and decides when a press has become a drag. A session
 * runs from {@link begin} to {@link end}; the overlay may be shown before
 * the drag is confirmed, as early feedback on what the press will do.
 */
export class WindowLevelDrag {
    /**
     * The {@link Environment} whose image the session adjusts: the pressed
     * view's, pinned from {@link begin} to {@link end}, so a drag on a grid
     * tile windows that row and no other.
     */
    private environment: Environment | undefined;

    /** Accumulated windowing delta since the session began
     *  (for cursor arrow direction). */
    private totalDelta = vec2.create();

    /** Snapshot of WW/WC before windowing (for cancel restore). */
    private savedWindowLevel: WindowLevel | undefined;

    /** Series UID that was snapshotted (for cancel restore). */
    private savedSeriesUid: string | undefined;

    /**
     * The side of the Environment's fused pair the session windows, as a
     * view of that side alone would: what the pressed view draws, or the
     * secondary for Alt on a blended view with a secondary loaded.
     */
    private targetDisplay = FusedImageDisplay.Blended;

    /** The aligned image the pressed view shows alone, when it shows one. */
    private targetAlignedImageKey: string | undefined;
    private viewFusion: ViewFusion | undefined;

    /** Whether the current session targets the secondary image. */
    private isTargetingSecondary = false;

    /** Custom windowing cursor overlay. */
    private windowingCursor: WindowingCursor | undefined;

    /**
     * 'none' while the overlay stands in for the pointer, so the system
     * cursor does not show beside it; undefined otherwise.
     */
    public get cursor(): string | undefined {
        return this.windowingCursor !== undefined ? 'none' : undefined;
    }

    /**
     * Begins a session at the press on a view of `environment`: pins that
     * environment and its target image for the session's duration and
     * snapshots the window level for {@link restoreWindowLevel}. Without an
     * environment the session has no target and applies nothing.
     *
     * The target is the image the pressed view draws: on a view that draws
     * one side of a fused pair alone, that side, whatever the modifiers; on
     * a blended view, the secondary while Alt is held and a secondary is
     * loaded, else the Environment's own windowing target, its primary by
     * default. A view that draws a side that is not loaded has no target.
     * A view with its own fusion (`viewFusion`) is read for its own pair.
     */
    public begin(
        environment: Environment | undefined,
        modifierKeys: ModifierKeys,
        display: FusedImageDisplay,
        alignedImageKey?: string,
        viewFusion?: ViewFusion
    ): void {
        this.environment = environment;
        this.targetAlignedImageKey = alignedImageKey;
        this.viewFusion = viewFusion;
        vec2.zero(this.totalDelta);

        const hasSecondary = this.hasLoadedSecondary();
        this.targetDisplay =
            display === FusedImageDisplay.Blended &&
            modifierKeys.hasAlt &&
            hasSecondary
                ? FusedImageDisplay.SecondaryOnly
                : display;
        this.isTargetingSecondary =
            hasSecondary &&
            this.targetDisplay === FusedImageDisplay.SecondaryOnly;

        const seriesUid = this.getTargetSeriesUid();
        if (seriesUid) {
            const wl = this.environment?.getImageWindowLevel(seriesUid);
            if (wl) {
                this.savedWindowLevel = wl.clone();
                this.savedSeriesUid = seriesUid;
            } else {
                console.warn(
                    'No window level found for the windowing target image'
                );
            }
        }
    }

    /** Shows the overlay at a client position, once per session. */
    public showCursor(clientX: number, clientY: number): void {
        if (this.windowingCursor) {
            return;
        }
        this.windowingCursor = new WindowingCursor(this.getCursorOptions());
        this.windowingCursor.show(clientX, clientY);
    }

    /**
     * Applies pointer movement as a window/level delta to the target
     * image and turns the overlay's arrows to follow the accumulated
     * direction. Nothing is published when no target image is loaded.
     * @param movement Raw movementX/movementY from the MouseEvent.
     */
    public applyDelta(movement: vec2): void {
        const seriesUid = this.getTargetSeriesUid();
        if (!seriesUid) {
            return;
        }

        const delta = vec2.create();
        const widthMovement =
            WINDOWING_CONFIG.widthAxis === 'x' ? movement[0] : movement[1];
        const centerMovement =
            WINDOWING_CONFIG.centerAxis === 'y' ? movement[1] : movement[0];

        if (WINDOWING_CONFIG.sensitivityMode === 'proportional') {
            delta[0] = widthMovement * WINDOWING_CONFIG.sensitivity;
            delta[1] = centerMovement * WINDOWING_CONFIG.sensitivity;
        } else {
            if (widthMovement > 0) {
                delta[0] = WINDOWING_CONFIG.stepSize;
            } else if (widthMovement < 0) {
                delta[0] = -WINDOWING_CONFIG.stepSize;
            }
            if (centerMovement > 0) {
                delta[1] = WINDOWING_CONFIG.stepSize;
            } else if (centerMovement < 0) {
                delta[1] = -WINDOWING_CONFIG.stepSize;
            }
        }

        if (delta[0] !== 0 || delta[1] !== 0) {
            this.totalDelta[0] += delta[0];
            this.totalDelta[1] += delta[1];
            this.windowingCursor?.updateDirection(this.totalDelta);

            domainEventBus.publishSync(
                new WindowLevelDeltaEvent(delta, seriesUid, 'user')
            );
        }
    }

    /**
     * Puts the window level snapshotted at {@link begin} back on the
     * target image. Nothing is published when no snapshot was taken.
     *
     * The restore is published with the source 'engine', which leaves the
     * image's record of a window the user chose (see
     * `Image3D.setWindowLevel`) alone. A delta never changes that record
     * either, so it stays as it was at {@link begin}: a cancelled drag leaves
     * no trace in it.
     *
     * A snapshot taken while the target volume loaded can carry the
     * placeholder level bounds of that load. When the load has completed
     * since, the volume is asked for the window of its completed load, so
     * such a snapshot does not stay on it.
     */
    public restoreWindowLevel(): void {
        if (this.savedWindowLevel && this.savedSeriesUid) {
            domainEventBus.publishSync(
                new WindowLevelSetEvent(
                    this.savedWindowLevel,
                    this.savedSeriesUid,
                    'engine'
                )
            );
            this.environment?.publishWindowOfCompletedLoad(this.savedSeriesUid);
        }
    }

    /** Ends the session: removes the overlay and drops the snapshot and target. */
    public end(): void {
        if (this.windowingCursor) {
            this.windowingCursor.hide();
            this.windowingCursor = undefined;
        }
        this.environment = undefined;
        this.targetDisplay = FusedImageDisplay.Blended;
        this.targetAlignedImageKey = undefined;
        this.isTargetingSecondary = false;
        this.savedWindowLevel = undefined;
        this.savedSeriesUid = undefined;
        vec2.zero(this.totalDelta);
    }

    // ───────── private helpers ─────────

    /**
     * Cursor appearance options matching the viewport HUD.
     * Returns color + label for primary/secondary, or empty
     * options (default white, no label) when no secondary
     * is loaded. When a colored fusion theme is active the
     * cursor uses the theme's color masks; for grayscale
     * fusion it falls back to the app's primary/secondary
     * theme colors.
     */
    private getCursorOptions(): WindowingCursorOptions {
        const hasSecondary = this.hasLoadedSecondary();
        if (!hasSecondary) {
            return {};
        }

        const imageLabel = this.isTargetingSecondary ? 'II' : 'I';

        const useThemeColors =
            this.environment !== undefined && !this.isGrayscaleColorMask();

        if (useThemeColors) {
            const masks = this.pairMasks()!;
            const mask = this.isTargetingSecondary
                ? masks.secondaryColorMask
                : masks.colorMask;
            return {
                foregroundColor: vec3ToHex(mask),
                imageLabel,
            };
        }

        const foregroundColor = this.isTargetingSecondary
            ? SECONDARY_COLOR
            : PRIMARY_COLOR;
        return { foregroundColor, imageLabel };
    }

    /**
     * Returns true when both color masks are [1, 1, 1] — the
     * default grayscale state where no colored fusion theme is
     * active.
     */
    private isGrayscaleColorMask(): boolean {
        const masks = this.pairMasks();
        if (!masks) {
            return true;
        }
        const primary = masks.colorMask;
        const secondary = masks.secondaryColorMask;
        return (
            primary[0] === 1 &&
            primary[1] === 1 &&
            primary[2] === 1 &&
            secondary[0] === 1 &&
            secondary[1] === 1 &&
            secondary[2] === 1
        );
    }

    /** The tints of the pressed view's pair: its own blend's, else its Environment's. */
    private pairMasks():
        | Pick<Environment, 'colorMask' | 'secondaryColorMask'>
        | undefined {
        if (!this.environment) {
            return undefined;
        }
        if (!this.viewFusion) {
            return this.environment;
        }
        const mapping = fusionColorMappingById(
            this.viewFusion.blend.colorMappingId
        );
        return {
            colorMask: mapping.primaryValues,
            secondaryColorMask: mapping.secondaryValues,
        };
    }

    /** Whether the pressed view's pair has a secondary image loaded. */
    private hasLoadedSecondary(): boolean {
        if (!this.environment) {
            return false;
        }
        return (
            fusedPairOf({
                environment: this.environment,
                viewFusion: this.viewFusion,
            }).secondaryImage?.parent?.seriesUid !== undefined
        );
    }

    /**
     * Returns the series UID of the image targeted by the drag, as
     * {@link begin} describes.
     */
    private getTargetSeriesUid(): string | undefined {
        if (!this.environment) {
            return undefined;
        }
        const environment = this.environment;
        return windowingSeriesUidFor(
            this.targetDisplay,
            this.viewFusion
                ? {
                      ...ownFusedPair(environment, this.viewFusion),
                      activeWindowingSeriesUid:
                          environment.activeWindowingSeriesUid,
                  }
                : environment,
            this.targetAlignedImageKey
        );
    }
}
